import React, { useContext, useState } from "react";
import { sum } from "lodash";
import { ProductOrder } from "../../../../data/entities";
import { EnhancedMaterial, EnhancedProduct } from "../types";
import AddProductModal from "../AddProductModal";
import { ContentWrapper, ProductButtonRow, ProductTableText } from "../Styled";
import { SummaryTableCell, SummaryTableRow } from "../AddProductModal/Styled";
import CustomTable from "../../../../shared/components/CustomTable";
import { ProductOrderProductTableColumns } from "../../../../configs/productOrder";
import { StyledButton } from "../../Styled";
import { IconButton, TableRow } from "@mui/material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../shared/components/TextBox";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { color } from "../../../../shared/styles";
import { TagName, WarningMessage } from "./Styled";
import WarningTableIcon from "../../../../shared/svg/WarningTableIcon";
import { formatCurrency, formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { ROLE } from "../../../../data/enums";
import TabBar from "../../../../shared/components/TabBar";
import { error } from "../../../../utils/toast";
import { getProductTabPolicy } from "./productTabPolicy";

interface ProductRowProps {
    item: any;
    extraRowProps: any;
}

const getSelectedQuantity = (item: any) => {
    return item.selectedQuantity !== undefined ? item.selectedQuantity : 0;
};

const getMissingQuantity = (item: any) => {
    return Math.abs(Math.min(0, item.quantity - getSelectedQuantity(item)));
};

const ProductRow = ({ item, extraRowProps }: ProductRowProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const quantity = getSelectedQuantity(item);
    const canSeePrice = [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role) === false;
    const isEditingAllowed = !extraRowProps?.isView;

    const isQuantitySufficient = item.missingQuantity === 0 || getMissingQuantity(item) === 0;
    const backgroundColor = isQuantitySufficient ? "" : color.status.DANGER_LIGHT;
    const selectedQuantityBorderColor = isQuantitySufficient ? "" : color.status.FAIL;

    const percentDiscount = item.discount !== undefined ? item.discount : 0;
    const percentMarkup = item.markup !== undefined ? item.markup : 0;

    const price = item.item_type === "material"
        ? (item.material_price !== undefined ? item.material_price : 0)
        : (item.product_price !== undefined ? item.product_price : 0);

    const updateMaterialPrice = (value: string) => {
        if (extraRowProps && extraRowProps.updateMaterialPrice) {
            extraRowProps.updateMaterialPrice(item.id, Number(value));
        }
    };

    if (extraRowProps?.isMissingTab) {
        // Render compact 5-column row for Missing Products tab
        return (
            <TableRow>
                <StyledTableCell align="left">
                    <StyledNavLink to={`/products/${item.id}`}>
                        {item.product_code || item.id}
                    </StyledNavLink>
                </StyledTableCell>
                <StyledTableCell align="left">
                    {item.name}
                    <TagName itemType={"product"}>Sản phẩm</TagName>
                </StyledTableCell>
                <StyledTableCell align="left">{item.available_qty ?? 0}</StyledTableCell>
                <StyledTableCell align="left">{item.missing_qty ?? 0}</StyledTableCell>
                <StyledTableCell align="left">
                    <TextBox
                        numeric
                        text={formatInternationalNumber(item.required_qty ?? "")}
                        onChangeText={() => {
                        }}
                        disabled={true}

                    />
                </StyledTableCell>
            </TableRow>
        );
    }

    return (
        <>
            {!item.isSummary ? (
                <TableRow sx={{ backgroundColor }}>
                    <StyledTableCell align="left" style={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: backgroundColor || 'white' }}>
                        <StyledNavLink to={`/${item.item_type === "material" ? "materials" : "products"}/${item.id}`}>
                            {item.product_code || item.material_code ? item.product_code || item.material_code : item.id}
                        </StyledNavLink>
                    </StyledTableCell>
                    <StyledTableCell align="left" style={{ position: 'sticky', left: 140, zIndex: 1, backgroundColor: backgroundColor || 'white' }}>
                        {item.name}
                        {item.item_type && (
                            <TagName itemType={item.item_type}>
                                {item.item_type === "product" ? "Sản phẩm" : "Vật tư"}
                            </TagName>
                        )}
                    </StyledTableCell>

                    {canSeePrice && (
                        <StyledTableCell align="left" style={{ position: 'sticky', left: 280, zIndex: 1, backgroundColor: backgroundColor || 'white' }}>
                            {!extraRowProps.isView ? (
                                <TextBox
                                    numeric
                                    style={{
                                        borderColor: selectedQuantityBorderColor,
                                        backgroundColor,
                                        width: "160px",
                                        minWidth: "160px",
                                    }}
                                    text={formatInternationalNumber(price ?? "")}
                                    onChangeText={(value: string) => {
                                        const n = parseInternationalToNumber(value, error);
                                        if (!Number.isFinite(n)) return;
                                        if (item.item_type === "material") {
                                            if (extraRowProps && extraRowProps.updateMaterialPrice) {
                                                extraRowProps.updateMaterialPrice(item.id, n);
                                            }
                                        } else {
                                            if (extraRowProps && extraRowProps.updateProductPrice) {
                                                extraRowProps.updateProductPrice(item, n);
                                            }
                                        }
                                    }}
                                    disabled={extraRowProps.isView}
                                />
                            ) : (
                                formatCurrency(price ?? "")
                            )}
                        </StyledTableCell>
                    )}

                    {canSeePrice && (
                        <StyledTableCell align="left">
                            {isEditingAllowed ? (
                                <TextBox
                                    numeric
                                    style={{
                                        borderColor: selectedQuantityBorderColor,
                                        backgroundColor,
                                    }}
                                    text={formatInternationalNumber(percentDiscount ?? "")}
                                    onChangeText={(value: string) => {
                                        const n = parseInternationalToNumber(value, error);
                                        if (!Number.isFinite(n)) return;
                                        extraRowProps.onChangeProductDiscount(item, n);
                                    }}
                                    disabled={extraRowProps.isView}
                                />
                            ) : (
                                `${(percentDiscount || 0).toString()}`
                            )}
                        </StyledTableCell>
                    )}

                    {canSeePrice && (
                        <StyledTableCell align="left">
                            {isEditingAllowed ? (
                                <TextBox
                                    numeric
                                    style={{
                                        borderColor: selectedQuantityBorderColor,
                                        backgroundColor,
                                    }}
                                    text={formatInternationalNumber(percentMarkup ?? "")}
                                    onChangeText={(value: string) => {
                                        const n = parseInternationalToNumber(value, error);
                                        if (!Number.isFinite(n)) return;
                                        extraRowProps.onChangeProductMarkup(item, n);
                                    }}
                                    disabled={extraRowProps.isView}
                                />
                            ) : (
                                `${(percentMarkup || 0).toString()}`
                            )}
                        </StyledTableCell>
                    )}

                    {canSeePrice && (
                        <StyledTableCell align="left">
                            {formatCurrency(
                                Math.ceil(((price * (100 - percentDiscount)) / 100) * ((100 + percentMarkup) / 100)),
                            )}
                        </StyledTableCell>
                    )}

                    <StyledTableCell align="left">{item.quantity}</StyledTableCell>
                    <StyledTableCell align="left">
                        <TextBox
                            numeric
                            style={{
                                borderColor: selectedQuantityBorderColor,
                                backgroundColor,
                            }}
                            text={formatInternationalNumber(quantity ?? "")}
                            onChangeText={(value: string) => {
                                const n = parseInternationalToNumber(value, error);
                                if (!Number.isFinite(n)) return;
                                extraRowProps.onChangeProductSelectedQuantity(item, n);
                            }}
                            disabled={extraRowProps.isView}
                        />
                    </StyledTableCell>
                    <StyledTableCell align="left">
                        {!extraRowProps.isView && (
                            <IconButton onClick={() => extraRowProps.removeItem(item)}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </StyledTableCell>
                </TableRow>
            ) : (
                <SummaryTableRow>
                    <SummaryTableCell align="left" style={{ position: 'sticky', left: 0, zIndex: 12, backgroundColor: color.secondary.LIGHT_GREY }}>{item.id}</SummaryTableCell>
                    <SummaryTableCell align="left" style={{ position: 'sticky', left: 140, zIndex: 12, backgroundColor: color.secondary.LIGHT_GREY }}>{item.name}</SummaryTableCell>
                    {canSeePrice && (
                        <SummaryTableCell align="left" style={{ position: 'sticky', left: 280, zIndex: 12, backgroundColor: color.secondary.LIGHT_GREY }}>{formatCurrency(item.totalOriginalPrice)}</SummaryTableCell>
                    )}
                    {canSeePrice && (
                        <SummaryTableCell align="left"></SummaryTableCell>
                    )}
                    {canSeePrice && (
                        <SummaryTableCell align="left"></SummaryTableCell>
                    )}
                    {canSeePrice && (
                        <SummaryTableCell align="left">{formatCurrency(item.totalDiscountPrice)}</SummaryTableCell>
                    )}
                    <SummaryTableCell align="left"></SummaryTableCell>
                    <SummaryTableCell align="left"></SummaryTableCell>
                    {/* Removed missing quantity summary column for SaleOrder detail */}
                    <SummaryTableCell align="left"></SummaryTableCell>
                </SummaryTableRow>
            )}
        </>
    );
};

interface ProductListProps {
    productOrder: ProductOrder;
    mergeProductOrder: Function;
    isView?: boolean;
    orderState?: string;
}

const ProductList = ({ productOrder, mergeProductOrder, isView, orderState = "OPEN" }: ProductListProps) => {
    const { products = [], materials = [] } = productOrder;
    const [isProductModalOpen, setProductModalOpen] = useState(false);
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const customer = productOrder.customer;
    const customerCategory = customer?.category;

    const canSeePrice = [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role) === false;

    const enhancedProducts = products.map((product: any) => ({
        ...product,
        line_item_id: product.line_item_id,
        client_uid: product.client_uid,
    })) as EnhancedProduct[];

    const enhancedMaterials = materials.map((material: any) => ({
        id: material.id,
        is_active: material.is_active,
        name: material.name,
        price: material.price,
        quantity: material.quantity,
        selectedQuantity: material.selectedQuantity,
        missingQuantity: material.missingQuantity,
        material_code: material.material_code,
        item_type: "material" as const,
        material_price: material.material_price,
        discount: material.discount,
        markup: material.markup,
        client_uid: (material as any).client_uid,
        line_item_id: material.line_item_id,
    })) as EnhancedMaterial[];

    const allItems = [...enhancedProducts, ...enhancedMaterials] as any[];

    const productCount = enhancedProducts.length;
    const materialCount = enhancedMaterials.length;

    const summaryProduct = {
        id: `Tổng ${allItems.length} mục`,
        name: "Subtotal",
        totalDiscountPrice: sum(
            allItems.map(
                (item: any) => {
                    const itemPrice = item.item_type === "material" ? item.material_price : item.product_price;
                    const discount = item.discount || 0;
                    const markup = item.markup || 0;
                    return getSelectedQuantity(item) *
                        Math.ceil(
                            ((itemPrice * (100 - discount)) / 100) * ((100 + markup) / 100),
                        );
                },
            ),
        ),
        totalOriginalPrice: sum(
            allItems.map((item: any) => {
                const itemPrice = item.item_type === "material" ? item.material_price : item.product_price;
                return getSelectedQuantity(item) * itemPrice;
            }),
        ),
        isSummary: true,
    };

    const tableItems = allItems.length === 0 ? [] : [...allItems, summaryProduct];

    const isSufficient = tableItems.every(
        (item: any) => item.isSummary || item.missingQuantity === 0 || getMissingQuantity(item) === 0,
    );

    const onRemoveProduct = (item: EnhancedProduct) => {

        if (item.item_type === "material") {
            // For items without line_item_id (newly added), use id + item_type for removal
            const removedMaterials = enhancedMaterials.filter((m: any) => {
                if ((item as any).line_item_id) {
                    return (m as any).line_item_id !== (item as any).line_item_id;
                } else {
                    return !(m.id === item.id && m.item_type === item.item_type);
                }
            });
            mergeProductOrder({
                materials: removedMaterials,
                products: enhancedProducts,
            });
        } else {
            // For items without line_item_id (newly added), use id + item_type for removal
            const removedProducts = enhancedProducts.filter((p: any) => {
                if ((item as any).line_item_id) {
                    return (p as any).line_item_id !== (item as any).line_item_id;
                } else {
                    return !(p.id === item.id && p.item_type === item.item_type);
                }
            });
            mergeProductOrder({
                products: removedProducts,
                materials: enhancedMaterials,
            });
        }
    };

    const onChangeProductDiscount = (item: any, discount: any) => {

        const updatedMaterials = enhancedMaterials.map((material: any) => {
            const matches =
                (item.line_item_id && material.line_item_id === item.line_item_id) ||
                (item.client_uid && material.client_uid === item.client_uid);
            if (matches) {
                return { ...material, discount };
            }
            return material;
        });

        const updatedProducts = enhancedProducts.map((product: any) => {
            const matches =
                (item.line_item_id && product.line_item_id === item.line_item_id) ||
                (item.client_uid && product.client_uid === item.client_uid);
            if (matches) {
                return { ...product, discount };
            }
            return product;
        });

        mergeProductOrder({
            products: updatedProducts,
            materials: updatedMaterials,
        });
    };

    const onChangeProductMarkup = (item: any, markup: any) => {

        const updatedMaterials = enhancedMaterials.map((material: any) => {
            const matches =
                (item.line_item_id && material.line_item_id === item.line_item_id) ||
                (item.client_uid && material.client_uid === item.client_uid);
            if (matches) {
                return { ...material, markup };
            }
            return material;
        });

        const updatedProducts = enhancedProducts.map((product: any) => {
            const matches =
                (item.line_item_id && product.line_item_id === item.line_item_id) ||
                (item.client_uid && product.client_uid === item.client_uid);
            if (matches) {
                return { ...product, markup };
            }
            return product;
        });

        mergeProductOrder({
            products: updatedProducts,
            materials: updatedMaterials,
        });
    };

    const onChangeProductSelectedQuantity = (item: any, quantity: any) => {

        const updatedMaterials = enhancedMaterials.map((material: any) => {
            const matches =
                (item.line_item_id && material.line_item_id === item.line_item_id) ||
                (item.client_uid && material.client_uid === item.client_uid);
            if (matches) {
                return { ...material, selectedQuantity: quantity };
            }
            return material;
        });

        const updatedProducts = enhancedProducts.map((product: any) => {
            const matches =
                (item.line_item_id && product.line_item_id === item.line_item_id) ||
                (item.client_uid && product.client_uid === item.client_uid);
            if (matches) {
                return { ...product, selectedQuantity: quantity };
            }
            return product;
        });

        mergeProductOrder({
            products: updatedProducts,
            materials: updatedMaterials,
        });
    };

    const updateMaterialPrice = (id: string, materialPrice: number) => {
        const updatedMaterials = enhancedMaterials.map((material: any) => {
            if (material.id === id) {
                return {
                    ...material,
                    material_price: materialPrice,
                };
            }
            return material;
        });

        mergeProductOrder({
            materials: updatedMaterials,
            products: enhancedProducts,
        });
    };

    const updateProductPrice = (item: any, productPrice: number) => {
        const updatedProducts = enhancedProducts.map((product: any) => {
            const matches =
                (item.line_item_id && product.line_item_id === item.line_item_id) ||
                (item.client_uid && product.client_uid === item.client_uid);
            if (matches) {
                return { ...product, product_price: productPrice };
            }
            return product;
        });
        mergeProductOrder({
            products: updatedProducts,
            materials: enhancedMaterials,
        });
    };

    const getColumns = () => {
        const baseColumns = ProductOrderProductTableColumns;
        const columns = baseColumns.filter((item) => item.text !== "Số lượng thiếu");

        if (!canSeePrice) {
            return columns.filter((item) =>
                item.text !== "Giá bán" &&
                item.text !== "Chiết khấu (%)" &&
                item.text !== "Thuế suất (%)" &&
                item.text !== "Giá sau chiết khấu",
            );
        }

        return columns;
    };

    const missingProducts = (productOrder as any)?.requirements?.missing_products || [];
    const productTabPolicy = getProductTabPolicy(role, missingProducts.length);
    const missingProductRows = missingProducts.map((mp: any) => {
        const product = enhancedProducts.find((p) => p.id === mp.product_id);
        return {
            id: mp.product_id,
            product_code: product?.product_code || "",
            name: product?.name || mp.product_id,
            available_qty: mp.available_qty,
            missing_qty: mp.missing_qty,
            required_qty: mp.required_qty,
            item_type: "product",
            isMissingRow: true,
        };
    });

    const MissingProductsTable = (
        <CustomTable
            itemList={missingProductRows}
            RowElement={ProductRow}
            TableColumns={[
                { text: "Mã sản phẩm", icon: <></> },
                { text: "Tên sản phẩm", icon: <></> },
                { text: "Tồn kho", icon: <></> },
                { text: "Số lượng thiếu", icon: <></> },
                { text: "Số lượng yêu cầu", icon: <></> },
            ]}
            extraRowProps={{
                isMissingTab: true,
                isView: true,
                orderState,
            }}
            containerStyle={{ maxHeight: "400px" }}
        />
    );

    return (
        <>
            {isProductModalOpen && !isView && (
                <AddProductModal
                    selectedProducts={[...enhancedProducts, ...enhancedMaterials] as any[]}
                    open={isProductModalOpen}
                    handleClose={() => setProductModalOpen(false)}
                    mergeProductOrder={mergeProductOrder}
                    onChangeProductSelectedQuantity={onChangeProductSelectedQuantity}
                    isView={isView}
                    customerId={customer?.id}
                    customerCategory={customerCategory}
                    enforceCommonCategory={productOrder.order_type !== "RESERVE"}
                    orderState={orderState}
                />
            )}

            <ContentWrapper>
                <ProductTableText>
                    Danh sách mục{" "}
                    <span
                        style={{
                            color: color.secondary.DARK_GREY,
                            fontSize: "16px",
                            fontWeight: "400",
                        }}
                    >
                        ({productCount} sản phẩm, {materialCount} vật tư)
                    </span>
                </ProductTableText>

                {!isSufficient && (
                    <WarningMessage>
                        <WarningTableIcon />
                        <span>
                            <span style={{ fontWeight: "700" }}>Lưu ý quan trọng: </span>
                            Một số sản phẩm trong danh sách vượt quá tồn kho cho phép. Bạn cần bổ sung số lượng sau khi tạo
                            đơn hàng.
                        </span>
                    </WarningMessage>
                )}

                {productTabPolicy.showTabs ? (
                    <TabBar
                        tabs={
                            productTabPolicy.showListTab
                                ? [
                                    {
                                        label: "Danh sách",
                                        labelEN: "list",
                                        content: (
                                            <CustomTable
                                                itemList={tableItems}
                                                RowElement={ProductRow}
                                                TableColumns={getColumns()}
                                                extraRowProps={{
                                                    removeItem: onRemoveProduct,
                                                    onChangeProductSelectedQuantity,
                                                    onChangeProductDiscount,
                                                    onChangeProductMarkup,
                                                    updateMaterialPrice,
                                                    updateProductPrice,
                                                    isView,
                                                    orderState,
                                                }}
                                                containerStyle={{ maxHeight: "400px" }}
                                            />
                                        ),
                                    },
                                    {
                                        label: "Thiếu sản phẩm",
                                        labelEN: "missing",
                                        content: MissingProductsTable,
                                    },
                                ]
                                : [
                                    {
                                        label: "Thiếu sản phẩm",
                                        labelEN: "missing",
                                        content: MissingProductsTable,
                                    },
                                ]
                        }
                        tabParamName={"sotab"}
                        defaultTab={productTabPolicy.defaultTab}
                    />
                ) : (
                    <CustomTable
                        itemList={tableItems}
                        RowElement={ProductRow}
                        TableColumns={getColumns()}
                        extraRowProps={{
                            removeItem: onRemoveProduct,
                            onChangeProductSelectedQuantity,
                            onChangeProductDiscount,
                            onChangeProductMarkup,
                            updateMaterialPrice,
                            updateProductPrice,
                            isView,
                            orderState,
                        }}
                        containerStyle={{ maxHeight: "400px" }}
                    />
                )}

                {allItems.length === 0 && "Không có mục được chọn"}
                {!isView && (
                    <ProductButtonRow>
                        <StyledButton variant="contained" onClick={() => setProductModalOpen(true)}>
                            Thêm mục
                        </StyledButton>
                    </ProductButtonRow>
                )}
            </ContentWrapper>
        </>
    );
};

export default ProductList;
